import type { NavigationItem } from '@/types/navigation';
import { z } from 'zod';
import { flattenNavigationItems, getSearchNavigationItems } from '@/lib/navigation-config';
import { SHOW_SETTINGS_API_GUIDE_NAV } from '@/lib/settings-ui-flags';
import { SCM_PLATFORM_CATALOG, SCM_PLATFORMS } from '@codemerlin/shared';
import { isScmPlatformFlagOn, SCM_ICON_NAMES } from '@/lib/scm-platforms';

/** Max eligible catalog rows per FR-004 / plan. */
export const SEARCH_PALETTE_CATALOG_MAX = 50;

/**
 * Zod shape aligned with specs/329-global-search-command-palette/contracts/catalog-entry.schema.json
 */
export const paletteCatalogEntrySchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    path: z.string().min(1),
    icon: z.string().min(1),
    group: z.string().optional(),
    keywords: z.array(z.string()).max(32).optional(),
    requiresOrganization: z.boolean().optional(),
    eligibleRoles: z.array(z.string()).optional(),
  })
  .strict();

export type SearchStaticItem = z.infer<typeof paletteCatalogEntrySchema>;

/** Strict parse for external/unknown catalog payloads (optional helper). */
export function parsePaletteCatalogEntry(input: unknown): SearchStaticItem {
  return paletteCatalogEntrySchema.parse(input);
}

export function safeParsePaletteCatalogEntry(input: unknown): SearchStaticItem | null {
  const r = paletteCatalogEntrySchema.safeParse(input);
  return r.success ? r.data : null;
}

const ADMIN_ROLES = ['admin', 'owner'] as const;

/**
 * Additional app pages not listed in the main sidebar.
 * `label` values are i18n keys resolved via the 'layout' namespace.
 */
function extraSearchRoutes(organizationId: string | undefined): SearchStaticItem[] {
  const org = organizationId;

  const routes: SearchStaticItem[] = [
    {
      id: 'profile',
      label: 'search.routes.profile',
      path: '/settings/profile',
      icon: 'User',
      keywords: ['account', 'me'],
      requiresOrganization: false,
    },
    {
      id: 'dashboards-teams',
      label: 'search.routes.teamsDashboard',
      path: '/dashboards/teams',
      icon: 'LayoutGrid',
      keywords: ['aggregate', 'metrics'],
      requiresOrganization: true,
    },
    {
      id: 'dashboards-repos',
      label: 'search.routes.repositoriesDashboard',
      path: '/dashboards/repositories',
      icon: 'LayoutGrid',
      keywords: ['aggregate', 'metrics'],
      requiresOrganization: true,
    },
    {
      id: 'teams-manage',
      label: 'search.routes.manageTeams',
      path: '/teams/manage',
      icon: 'Users',
      keywords: ['crud', 'members'],
      requiresOrganization: true,
      eligibleRoles: [...ADMIN_ROLES],
    },
    {
      id: 'settings-repos',
      label: 'search.routes.repositoryConnections',
      path: '/settings/repositories',
      icon: 'FolderGit2',
      keywords: ['scm', 'github', 'gitlab', 'integrations'],
      requiresOrganization: true,
      eligibleRoles: [...ADMIN_ROLES],
    },
    {
      id: 'settings-work-items',
      label: 'search.routes.workItemIntegrations',
      path: '/settings/work-items',
      icon: 'CheckSquare',
      keywords: [
        'jira',
        'linear',
        'github issues',
        ...(isScmPlatformFlagOn('azure_devops') ? ['azure boards'] : []),
      ],
      requiresOrganization: true,
      eligibleRoles: [...ADMIN_ROLES],
    },
    {
      id: 'settings-billing',
      label: 'search.routes.billingUsage',
      path: '/settings/billing',
      icon: 'CreditCard',
      keywords: ['subscription', 'invoice', 'cost'],
      requiresOrganization: true,
      eligibleRoles: [...ADMIN_ROLES],
    },
    ...(SHOW_SETTINGS_API_GUIDE_NAV
      ? [
          {
            id: 'settings-api-guide',
            label: 'search.routes.apiGuide',
            path: '/settings/api-guide',
            icon: 'BookOpen',
            keywords: ['openapi', 'documentation', 'rest'],
            requiresOrganization: true,
            eligibleRoles: [...ADMIN_ROLES],
          } satisfies SearchStaticItem,
        ]
      : []),
    {
      id: 'settings-github-register',
      label: 'search.routes.githubAppRegistration',
      path: '/settings/git?platform=github-app',
      icon: 'Github',
      keywords: ['install', 'app'],
      requiresOrganization: true,
      eligibleRoles: [...ADMIN_ROLES],
    },
    ...(isScmPlatformFlagOn('gitlab')
      ? [
          {
            id: 'settings-gitlab',
            label: 'search.routes.gitlabConfiguration',
            path: '/settings/git?platform=gitlab&section=byoc',
            icon: 'GitBranch',
            keywords: ['self-hosted'],
            requiresOrganization: true,
            eligibleRoles: [...ADMIN_ROLES],
          } satisfies SearchStaticItem,
        ]
      : []),
    // Every other flag-on platform: its settings panel (labels are one key per platform).
    ...SCM_PLATFORMS.filter(
      (platform) => platform !== 'github' && platform !== 'gitlab' && isScmPlatformFlagOn(platform)
    ).map(
      (platform): SearchStaticItem => ({
        id: `settings-${platform}`,
        label: `search.routes.scmSettings.${platform}`,
        path: `/settings/git?platform=${platform}`,
        icon: SCM_ICON_NAMES[SCM_PLATFORM_CATALOG[platform].iconKey],
        keywords: ['scm', 'integrations'],
        requiresOrganization: true,
        eligibleRoles: [...ADMIN_ROLES],
      })
    ),
    {
      id: 'style-guide',
      label: 'search.routes.styleGuide',
      path: '/style-guide',
      icon: 'Palette',
      keywords: ['design', 'ui'],
      requiresOrganization: false,
    },
  ];

  if (org) {
    routes.push(
      {
        id: 'org-members',
        label: 'search.routes.organizationMembers',
        path: `/organization/${org}/members`,
        icon: 'Users',
        keywords: ['people', 'roles', 'invite'],
        requiresOrganization: true,
        eligibleRoles: [...ADMIN_ROLES],
      },
      {
        id: 'org-audit',
        label: 'search.routes.auditLogs',
        path: `/organization/${org}/audit-logs`,
        icon: 'ScrollText',
        keywords: ['compliance', 'history'],
        requiresOrganization: true,
        eligibleRoles: [...ADMIN_ROLES],
      },
      {
        id: 'org-notifications',
        label: 'search.routes.notifications',
        path: `/organization/${org}/notifications`,
        icon: 'Bell',
        keywords: ['alerts', 'inbox'],
        requiresOrganization: true,
      }
    );
  }

  return routes;
}

function navItemToSearchItem(item: NavigationItem): SearchStaticItem {
  return {
    id: `nav-${item.id}`,
    label: item.label,
    path: item.path,
    icon: item.icon,
    requiresOrganization: true,
  };
}

function dedupeSearchItemsById(items: SearchStaticItem[]): SearchStaticItem[] {
  const seen = new Set<string>();
  const out: SearchStaticItem[] = [];
  for (const item of items) {
    if (seen.has(item.id)) {
      continue;
    }
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

function isRoleEligible(item: SearchStaticItem, memberRole: string | undefined): boolean {
  if (!item.eligibleRoles || item.eligibleRoles.length === 0) {
    return true;
  }

  const normalizedRole = memberRole?.toLowerCase();
  return normalizedRole ? item.eligibleRoles.includes(normalizedRole) : false;
}

/**
 * Curated palette catalog: sidebar destinations for the current shell (Next vs legacy)
 * plus extra routes, deduped by stable {@link SearchStaticItem.id} (so multiple labels can
 * share one path) and capped at {@link SEARCH_PALETTE_CATALOG_MAX}.
 */
export function getStaticSearchItems(
  organizationId: string | undefined,
  eligibleForNextShell: boolean,
  memberRole?: string
): SearchStaticItem[] {
  const navItems = flattenNavigationItems(getSearchNavigationItems(eligibleForNextShell)).filter(
    (i) => !i.disabled
  );
  const fromNav = navItems.map(navItemToSearchItem);
  const extras = extraSearchRoutes(organizationId);
  const merged = dedupeSearchItemsById([...fromNav, ...extras]);

  const orgScoped = (item: SearchStaticItem) => item.requiresOrganization !== false;

  const eligible =
    organizationId === undefined || organizationId === ''
      ? merged.filter((item) => !orgScoped(item))
      : merged;

  return eligible
    .filter((item) => isRoleEligible(item, memberRole))
    .slice(0, SEARCH_PALETTE_CATALOG_MAX);
}

export function staticItemMatchesQuery(item: SearchStaticItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) {
    return true;
  }
  if (item.label.toLowerCase().includes(q)) {
    return true;
  }
  if (item.path.toLowerCase().includes(q)) {
    return true;
  }
  if (item.keywords?.some((k) => k.toLowerCase().includes(q))) {
    return true;
  }
  return false;
}
